import { Link } from "react-router-dom";
import { formatUSDT, formatOdds } from "@/lib/contracts";
import { useMarketOdds } from "@/hooks/useMarkets";
import CategoryTag from "@/components/ui/CategoryTag";
import { categorize } from "@/lib/categories";

interface MarketCardProps {
  address: `0x${string}`;
  question: string;
  endTime: bigint;
  totalYesBets: bigint;
  totalNoBets: bigint;
  isResolved: boolean;
  winningOutcome: boolean;
  isOpen: boolean;
}

export default function MarketCard({
  address,
  question,
  endTime,
  totalYesBets,
  totalNoBets,
  isResolved,
  winningOutcome,
  isOpen,
}: MarketCardProps) {
  const { data: odds } = useMarketOdds(address);
  const totalPool = totalYesBets + totalNoBets;
  const endDate = new Date(Number(endTime) * 1000);
  const isExpired = endDate < new Date();

  // Calculate percentage for progress bar
  const yesPercent = totalPool > 0n
    ? Number((totalYesBets * 100n) / totalPool)
    : 50;

  const getStatusBadge = () => {
    if (isResolved) {
      return (
        <span className={`badge ${winningOutcome ? "badge-green" : "badge-red"}`}>
          Resolved: {winningOutcome ? "YES" : "NO"}
        </span>
      );
    }
    if (!isOpen || isExpired) {
      return <span className="badge badge-yellow">Closed</span>;
    }
    return <span className="badge badge-green">Open</span>;
  };

  const formatTimeRemaining = () => {
    if (isResolved || isExpired) return null;

    const now = new Date();
    const diff = endDate.getTime() - now.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    if (days > 0) return `${days}d ${hours}h remaining`;
    if (hours > 0) return `${hours}h remaining`;
    return "Ending soon";
  };

  return (
    <Link to={`/market/${address}`} className="block">
      <div className="card hover:shadow-md transition-shadow duration-200">
        <div className="p-5">
          {/* Header */}
          <div className="flex justify-between items-start mb-3">
            <div className="flex items-center gap-2 flex-wrap">
              {getStatusBadge()}
              <CategoryTag category={categorize(question)} />
            </div>
            {formatTimeRemaining() && (
              <span className="text-xs text-gray-500">{formatTimeRemaining()}</span>
            )}
          </div>

          {/* Question */}
          <h3 className="text-lg font-semibold text-gray-900 mb-4 line-clamp-2">
            {question}
          </h3>

          {/* Odds Display */}
          <div className="flex gap-3 mb-4">
            <div className="flex-1 bg-success/10 rounded-lg p-3 text-center">
              <div className="text-xs text-success font-medium mb-1">YES</div>
              <div className="num text-lg font-bold text-success">
                {odds ? formatOdds(odds[0]) : "-"}
              </div>
            </div>
            <div className="flex-1 bg-danger/10 rounded-lg p-3 text-center">
              <div className="text-xs text-danger font-medium mb-1">NO</div>
              <div className="num text-lg font-bold text-danger">
                {odds ? formatOdds(odds[1]) : "-"}
              </div>
            </div>
          </div>

          {/* Pool Progress Bar */}
          <div className="mb-3">
            <div className="flex justify-between text-xs text-gray-500 mb-1">
              <span>YES: {formatUSDT(totalYesBets)}</span>
              <span>NO: {formatUSDT(totalNoBets)}</span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden flex">
              <div
                className="bg-success transition-all duration-300"
                style={{ width: `${yesPercent}%` }}
              />
              <div
                className="bg-danger transition-all duration-300"
                style={{ width: `${100 - yesPercent}%` }}
              />
            </div>
          </div>

          {/* Total Pool */}
          <div className="text-center text-sm text-gray-600">
            Total Pool: <span className="num font-semibold">{formatUSDT(totalPool)} USDT</span>
          </div>
        </div>
      </div>
    </Link>
  );
}
